import { createContext, PropsWithChildren, useContext, useState } from 'react'
import { noop } from 'lodash'

import { PLATFORM, STREAMS_PLATFORM_ENABLED } from '@/constants/platform'
import { DGEAuthMethod, Platform } from '@/types/platform'
import { getLocalSessionItem, LocalSessionKey } from '@/utils/_legacy/localStorageItem'

// A platform remembered from before Streams was disabled must not resurrect the hidden
// tab, so fall back to Data in that case.
const rememberedPlatform = (): Platform => {
  const stored = getLocalSessionItem(LocalSessionKey.PLATFORM) as Platform

  return !STREAMS_PLATFORM_ENABLED && stored === PLATFORM.DGE ? PLATFORM.DDE : stored
}

const asyncNoop = async () => {
  // no operation
}

export type SignInType = (
  email: string,
  password: string,
  platform: Platform,
  authMethod?: DGEAuthMethod
) => Promise<void>
export type ForgotPasswordType = (email: string, platform: Platform) => Promise<void>
export enum LoginForgotPassActiveForm {
  LOGIN = 'login',
  FORGOT_PASSWORD = 'forgotPassword'
}

type CredentialsProvider = { signIn: SignInType; forgotPassword: ForgotPasswordType }
type CredentialsContext = {
  platform: Platform
  form: LoginForgotPassActiveForm
  onPlatformUpdate: (platform: Platform) => void
  onActiveFormUpdate: (form: LoginForgotPassActiveForm) => void
} & CredentialsProvider

const CredentialsContext = createContext<CredentialsContext>({
  platform: rememberedPlatform(),
  form: LoginForgotPassActiveForm.LOGIN,
  signIn: asyncNoop,
  forgotPassword: asyncNoop,
  onActiveFormUpdate: noop,
  onPlatformUpdate: noop
})

export const useCredentials = () => useContext(CredentialsContext)

export const CredentialsProvider = ({
  children,
  signIn,
  forgotPassword
}: PropsWithChildren<CredentialsProvider>) => {
  const [platform, setPlatform] = useState(rememberedPlatform() || PLATFORM.DDE)
  const [activeForm, setActiveForm] = useState<LoginForgotPassActiveForm>(
    LoginForgotPassActiveForm.LOGIN
  )

  const onPlatformUpdate = (platform: Platform) => {
    setPlatform(platform)
  }
  const onActiveFormUpdate = (form: LoginForgotPassActiveForm) => setActiveForm(form)

  return (
    <CredentialsContext.Provider
      value={{
        platform,
        form: activeForm,
        signIn,
        forgotPassword,
        onPlatformUpdate,
        onActiveFormUpdate
      }}
    >
      {children}
    </CredentialsContext.Provider>
  )
}
